import { useCallback, useEffect, useRef, useState } from 'react';
import type { SongRequest } from '../services/requests';
import { createLocalFileDeck, type LocalFileDeck } from '../services/localFilePlayback';
import { decideFileHandoff, newFileFadeInMs, type QueueLevel } from '../utils/localFileLevels';

const BRIDGE_MISSED_HANDOFF_MS = 3_000;

interface UseLocalFileDeckInput {
  eventId: string | undefined;
  nowPlaying: SongRequest | null;
  nextUp: SongRequest | null;
  isPlayerDevice: boolean;
  level: QueueLevel;
  crossfadeMs: number;
  onCrossfadeToNext(nextId: string): void;
  onTellBridgePlay(): void;
  onFileEndedChecklist(): void;
  onError(message: string): void;
}

export interface LocalFileDeckState {
  timeLeftMs: number | null;
  playingRequestId: string | null;
  blocked: boolean;
  paused: boolean;
  bridgeMissedHandoff: boolean;
  pauseFile(): void;
  resumeFile(): void;
  skipFile(): void;
  unblock(): void;
}

function localFileUrl(request: SongRequest | null): string | null {
  return request?.localFile?.storageUrl || null;
}

export function useLocalFileDeck(input: UseLocalFileDeckInput): LocalFileDeckState {
  const inputRef = useRef(input);
  inputRef.current = input;

  const [timeLeftMs, setTimeLeftMs] = useState<number | null>(null);
  const [playingRequestId, setPlayingRequestId] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [paused, setPaused] = useState(false);
  const [bridgeMissedHandoff, setBridgeMissedHandoff] = useState(false);

  const playingIdRef = useRef<string | null>(null);
  const lastLocalIdRef = useRef<string | null>(null);
  const lastNowPlayingKindRef = useRef<'local' | 'player' | null>(null);
  const timeLeftRef = useRef<number | null>(null);
  const handedOffRef = useRef<string | null>(null);
  const missedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deckRef = useRef<LocalFileDeck | null>(null);

  const clearMissedTimer = useCallback(() => {
    if (missedTimerRef.current) {
      clearTimeout(missedTimerRef.current);
      missedTimerRef.current = null;
    }
  }, []);

  const runHandoff = useCallback((msLeft: number, forced: boolean) => {
    const current = inputRef.current;
    const playingId = playingIdRef.current;
    if (!playingId) return;
    const next = current.nextUp
      ? { id: current.nextUp.id, isLocalFile: Boolean(localFileUrl(current.nextUp)) }
      : null;
    const decision = decideFileHandoff({
      timeLeftMs: msLeft,
      crossfadeMs: current.crossfadeMs,
      level: current.level,
      next,
      alreadyHandedOff: handedOffRef.current === playingId,
    });

    if (decision.kind === 'crossfade-to-next-file') {
      handedOffRef.current = playingId;
      current.onCrossfadeToNext(decision.nextId);
    } else if (decision.kind === 'tell-bridge-play') {
      handedOffRef.current = playingId;
      current.onTellBridgePlay();
    } else if (decision.kind === 'show-press-play-countdown' && forced) {
      handedOffRef.current = playingId;
      current.onFileEndedChecklist();
    }
  }, []);

  const getDeck = useCallback((): LocalFileDeck => {
    if (!deckRef.current) {
      deckRef.current = createLocalFileDeck({
        onTimeLeft(requestId, msLeft) {
          if (requestId !== playingIdRef.current) return;
          const previous = timeLeftRef.current;
          timeLeftRef.current = msLeft;
          if (previous === null || Math.ceil(previous / 1000) !== Math.ceil(msLeft / 1000)) {
            setTimeLeftMs(msLeft);
          }
          runHandoff(msLeft, false);
        },
        onEnded(requestId) {
          if (requestId !== playingIdRef.current) return;
          timeLeftRef.current = 0;
          setTimeLeftMs(0);
          runHandoff(0, false);
          if (inputRef.current.level === 'checklist') {
            if (handedOffRef.current !== requestId) inputRef.current.onFileEndedChecklist();
            return;
          }
          clearMissedTimer();
          missedTimerRef.current = setTimeout(() => {
            if (playingIdRef.current === requestId) setBridgeMissedHandoff(true);
          }, BRIDGE_MISSED_HANDOFF_MS);
        },
        onBlocked(requestId) {
          if (requestId === playingIdRef.current) setBlocked(true);
        },
        onError(requestId, message) {
          console.error(`Local file ${requestId} failed:`, message);
          if (requestId === playingIdRef.current) inputRef.current.onError(message);
        },
      });
    }
    return deckRef.current;
  }, [clearMissedTimer, runHandoff]);

  const resetPlayingState = useCallback(() => {
    playingIdRef.current = null;
    timeLeftRef.current = null;
    clearMissedTimer();
    setPlayingRequestId(null);
    setTimeLeftMs(null);
    setPaused(false);
    setBlocked(false);
    setBridgeMissedHandoff(false);
  }, [clearMissedTimer]);

  const { isPlayerDevice, crossfadeMs } = input;
  const nowPlayingId = input.nowPlaying?.id ?? null;
  const nowPlayingUrl = localFileUrl(input.nowPlaying);

  useEffect(() => {
    const deck = getDeck();
    if (!isPlayerDevice) {
      deck.stopAll();
      resetPlayingState();
      return;
    }

    const previousId = playingIdRef.current;
    const nextLocalId = nowPlayingUrl ? nowPlayingId : null;
    const cameFromPlayerSong = lastNowPlayingKindRef.current === 'player';
    if (nowPlayingId) lastNowPlayingKindRef.current = nowPlayingUrl ? 'local' : 'player';
    if (previousId === nextLocalId) return;

    if (previousId) {
      const msLeft = timeLeftRef.current ?? crossfadeMs;
      deck.fadeOutAndStop(previousId, Math.min(crossfadeMs, msLeft));
    }

    resetPlayingState();
    if (!nextLocalId || !nowPlayingUrl) return;

    // Now Playing can pass through empty between the "played" and "nowPlaying" writes,
    // so the file still fading out is remembered separately from the one playing
    const fadingId = lastLocalIdRef.current;
    const fadeInMs = newFileFadeInMs({
      previousFileStillSounding: Boolean(fadingId && fadingId !== nextLocalId && deck.isPlaying(fadingId)),
      cameFromPlayerSong,
      level: inputRef.current.level,
      crossfadeMs,
    });

    lastLocalIdRef.current = nextLocalId;
    playingIdRef.current = nextLocalId;
    setPlayingRequestId(nextLocalId);
    void deck.play(nextLocalId, nowPlayingUrl, fadeInMs);
  }, [nowPlayingId, nowPlayingUrl, isPlayerDevice, crossfadeMs, getDeck, resetPlayingState]);

  const nextUpId = input.nextUp?.id ?? null;
  const nextUpUrl = localFileUrl(input.nextUp);

  useEffect(() => {
    if (!isPlayerDevice || !nextUpId || !nextUpUrl) return;
    getDeck().preload(nextUpId, nextUpUrl);
  }, [nextUpId, nextUpUrl, isPlayerDevice, getDeck]);

  useEffect(() => {
    return () => {
      clearMissedTimer();
      deckRef.current?.stopAll();
    };
  }, [clearMissedTimer]);

  const pauseFile = useCallback(() => {
    const playingId = playingIdRef.current;
    if (!playingId) return;
    getDeck().pause(playingId);
    setPaused(true);
  }, [getDeck]);

  const resumeFile = useCallback(() => {
    const playingId = playingIdRef.current;
    if (!playingId) return;
    void getDeck().resume(playingId);
    setPaused(false);
  }, [getDeck]);

  const skipFile = useCallback(() => {
    runHandoff(inputRef.current.crossfadeMs, true);
  }, [runHandoff]);

  const unblock = useCallback(() => {
    setBlocked(false);
    void getDeck().retryBlocked();
  }, [getDeck]);

  if (!isPlayerDevice) {
    return {
      timeLeftMs: null,
      playingRequestId: null,
      blocked: false,
      paused: false,
      bridgeMissedHandoff: false,
      pauseFile,
      resumeFile,
      skipFile,
      unblock,
    };
  }

  return { timeLeftMs, playingRequestId, blocked, paused, bridgeMissedHandoff, pauseFile, resumeFile, skipFile, unblock };
}
